import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate, useBlocker } from "react-router-dom";
import { useWebSockets } from "../../../contexts/WebSocketContext";

export const useAssetGeneration = () => {
  const { id: publicationId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const intervalRef = useRef(null);

  // Context hooks
  const { activeTasks, setActiveTasks, taskStatus, setTaskStatus, setupWebSocket, setCurrentPublicationId, isTaskForCurrentPublication, getActiveTaskProgress } = useWebSockets();

  // Ensure dynamically-added task types (ppt_merge, video_merge, video_regenerate, extraction tasks) are always included
  const allTaskTypes = useMemo(() => {
    const baseTypes = ['video', 'ppt', 'poster', 'audio', 'carousel', 'instapost', 'post', 'images'];
    const dynamicTypes = ['ppt_merge', 'video_merge', 'video_regenerate', 'templates_llm', 'video_templates_llm'];
    const extraFromActive = Object.keys(activeTasks).filter(k => !baseTypes.includes(k) && !dynamicTypes.includes(k) && k !== 'chat');
    const extraFromStatus = Object.keys(taskStatus).filter(k => !baseTypes.includes(k) && !dynamicTypes.includes(k) && k !== 'chat');
    return [...new Set([...baseTypes, ...dynamicTypes, ...extraFromActive, ...extraFromStatus])];
  }, [activeTasks, taskStatus]);

  // Filter tasks to only show those for the current publication - use useMemo for reactivity
  const currentPublicationActiveTasks = useMemo(() => {
    const filtered = {};
    allTaskTypes.forEach(taskType => {
      // Ignore tasks with "unknown" ID as they don't have a valid project ID
      const hasValidActiveTask = (activeTasks[taskType] || []).some(id => {
        if (id === "unknown") return false;
        let task;
        if (id === 'legacy') {
          task = taskStatus[taskType];
        } else {
          task = taskStatus[taskType]?.[id];
        }
        if (!task || Number(task.publicationId) !== Number(publicationId)) return false;

        if (taskType.includes('ppt') && !task.pptId) return false;
        if (taskType.includes('video') && !task.video_project_id) return false;

        return true;
      });
      filtered[taskType] = hasValidActiveTask;
    });
    return filtered;
  }, [activeTasks, taskStatus, publicationId, allTaskTypes]);

  const currentPublicationTaskStatus = useMemo(() => {
    const filtered = {};
    allTaskTypes.forEach(taskType => {
      const activeInfo = getActiveTaskProgress(taskType, publicationId);
      const tInfo = activeInfo.id && activeInfo.id !== "unknown" ? taskStatus[taskType]?.[activeInfo.id] : null;

      const isValidInfo = tInfo && !(taskType.includes('ppt') && !tInfo.pptId) && !(taskType.includes('video') && !tInfo.video_project_id);

      // Filter out "unknown" IDs when determining active task info
      if (isValidInfo) {
        filtered[taskType] = tInfo;
      } else {
        filtered[taskType] = {
          progress: 0,
          status: "",
          fileId: null,
          publicationId: null,
          ...(taskType === 'ppt' && { pptId: null }),
          ...(taskType === 'video' && { video_project_id: null })
        };
      }
    });
    return filtered;
  }, [taskStatus, publicationId, getActiveTaskProgress, allTaskTypes]);

  // Alert state
  const [alert, setAlert] = useState(null);

  // Download IDs
  const [videoDownloadId, setVideoDownloadId] = useState(null);
  const [pptDownloadId, setPptDownloadId] = useState(null);
  const [posterDownloadId, setPosterDownloadId] = useState(null);
  const [audioDownloadId, setAudioDownloadId] = useState(null);
  const [imageDownloadId, setImageDownloadId] = useState(null);
  const [carouselDownloadId, setCarouselDownloadId] = useState(null);
  const [instapostDownloadId, setInstapostDownloadId] = useState(null);

  // Abstract Video States
  const [openAbstractVideoDialog, setOpenAbstractVideoDialog] = useState(false);
  const [videoDuration, setVideoDuration] = useState("");
  const [language, setLanguage] = useState("English");
  const [selectedColor, setSelectedColor] = useState("");
  const [customColor, setCustomColor] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [abstractVideoSrc, setAbstractVideoSrc] = useState("");
  const [openVideoViewDialog, setOpenVideoViewDialog] = useState(false);
  const [openVideoEditorView, setOpenVideoEditorView] = useState(false);
  const [videoContentLevel, setVideoContentLevel] = useState("Medical Affairs");
  const [videoSelectedSections, setVideoSelectedSections] = useState([]);
  const [videoProjectId, setVideoProjectId] = useState(null);

  // PPT (Presentation) States
  const [openPresentationDialog, setOpenPresentationDialog] = useState(false);
  const [slidesCount, setSlidesCount] = useState("short");
  const [presentationLanguage, setPresentationLanguage] = useState("English");
  const [speakerNotes, setSpeakerNotes] = useState("Yes");
  const [contentLevel, setContentLevel] = useState("Medical Affairs");
  const [presentationSelectedColor, setPresentationSelectedColor] = useState("");
  const [presentationCustomColor, setPresentationCustomColor] = useState("");
  const [pptUrl, setPptUrl] = useState("");
  const [openPptViewDialog, setOpenPptViewDialog] = useState(false);
  const [selectedSections, setSelectedSections] = useState([]);
  const [openSlideEditorView, setOpenSlideEditorView] = useState(false);
  const [generatedSlides, setGeneratedSlides] = useState([]);
  const [publicationTitle, setPublicationTitle] = useState("");
  const [slideGenerationProgress, setSlideGenerationProgress] = useState(0);
  const [isGeneratingSlides, setIsGeneratingSlides] = useState(false);
  const [pptId, setPptId] = useState(null); // Store pptId for fetching content

  // Poster States
  const [openPosterDialog, setOpenPosterDialog] = useState(false);
  const [posterSections, setPosterSections] = useState({
    abstract: false,
    methods: false,
    studyDesign: false,
    safety: false,
    adverseEvents: false,
  });
  const [posterLanguage, setPosterLanguage] = useState("English");
  const [addReferences, setAddReferences] = useState("No");
  const [posterContentLevel, setPosterContentLevel] = useState("Medical Affairs");
  const [posterSelectedColor, setPosterSelectedColor] = useState("");
  const [posterCustomColor, setPosterCustomColor] = useState("");
  const [posterUrl, setPosterUrl] = useState("");
  const [openPosterViewDialog, setOpenPosterViewDialog] = useState(false);

  // Audio Summary States
  const [openAudioSummaryDialog, setOpenAudioSummaryDialog] = useState(false);
  const [audioDuration, setAudioDuration] = useState("short");
  const [audioLanguage, setAudioLanguage] = useState("English");
  const [audioVoice, setAudioVoice] = useState("Male");
  const [audioLevel, setAudioLevel] = useState("Medical Affairs");
  const [audioSummaryUrl, setAudioSummaryUrl] = useState("");
  const [openAudioViewDialog, setOpenAudioViewDialog] = useState(false);

  // Figures (Images) States
  const [allFigures, setAllFigures] = useState([]);
  const [openImagesDialog, setOpenImagesDialog] = useState(false);

  // Carousel States
  const [carouselUrl, setCarouselUrl] = useState("");
  const [openCarouselViewDialog, setOpenCarouselViewDialog] = useState(false);

  // Instapost (Post) States
  const [instapostUrl, setInstapostUrl] = useState("");
  const [openInstapostViewDialog, setOpenInstapostViewDialog] = useState(false);
  const [openInstapostDialog, setOpenInstapostDialog] = useState(false);
  const [postSection, setPostSection] = useState("Introduction");
  const [postContentLevel, setPostContentLevel] = useState("Moderate");
  const [postSelectedColor, setPostSelectedColor] = useState("");
  const [postCustomColor, setPostCustomColor] = useState("");

  // Check if any generation is in progress FOR THE CURRENT PUBLICATION
  const isGenerating = Object.entries(currentPublicationTaskStatus).some(
    ([type, task]) => {

      // Ignore chat tasks so they don't block navigation
      if (type === "chat") return false;

      // "queued" and "generating" are always active states
      if (task.status === "queued" || task.status === "generating" || task.status === "merging") return true;

      // For merge tasks, "processing" means it is actively stitching the video/ppt together.
      // (For regular tasks, "processing" means it's paused in Review Content mode).
      // if ((type === "video_merge" || type === "ppt_merge") && task.status === "processing") return true;

      return false;
    }
  );

  // Block navigation if generating
  const blocker = useBlocker(({ nextLocation }) => isGenerating);

  // Auto-dismiss alert after 5 seconds
  useEffect(() => {
    if (alert) {
      const timer = setTimeout(() => {
        setAlert(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  // Update current publication ID whenever publicationId changes
  useEffect(() => {
    if (publicationId && setCurrentPublicationId) {
      setCurrentPublicationId(publicationId);
    }
  }, [publicationId, setCurrentPublicationId]);

  useEffect(() => {
    // Check video task for errors
    if (taskStatus.video?.status === "error") {
      setVideoProjectId(null);
    }

    // Check ppt task for errors
    if (taskStatus.ppt?.status === "error") {
      setPptId(null);
    }
  }, [taskStatus]);


  return {
    // Basic info
    publicationId,
    navigate,
    token,
    BASE_URL,
    intervalRef,

    // Context - use filtered versions for current publication
    activeTasks: currentPublicationActiveTasks,
    setActiveTasks,
    taskStatus: currentPublicationTaskStatus,
    rawTaskStatus: taskStatus,
    setTaskStatus,
    setupWebSocket,

    // Alert
    alert,
    setAlert,

    // Download IDs
    videoDownloadId,
    setVideoDownloadId,
    pptDownloadId,
    setPptDownloadId,
    posterDownloadId,
    setPosterDownloadId,
    audioDownloadId,
    setAudioDownloadId,
    imageDownloadId,
    setImageDownloadId,
    carouselDownloadId,
    setCarouselDownloadId,
    instapostDownloadId,
    setInstapostDownloadId,

    // Video states
    openAbstractVideoDialog,
    setOpenAbstractVideoDialog,
    videoDuration,
    setVideoDuration,
    language,
    setLanguage,
    selectedColor,
    setSelectedColor,
    customColor,
    setCustomColor,
    logoFile,
    setLogoFile,
    abstractVideoSrc,
    setAbstractVideoSrc,
    openVideoViewDialog,
    setOpenVideoViewDialog,
    openVideoEditorView,
    setOpenVideoEditorView,
    videoContentLevel,
    setVideoContentLevel,
    videoSelectedSections,
    setVideoSelectedSections,
    videoProjectId,
    setVideoProjectId,

    // PPT states
    openPresentationDialog,
    setOpenPresentationDialog,
    slidesCount,
    setSlidesCount,
    presentationLanguage,
    setPresentationLanguage,
    speakerNotes,
    setSpeakerNotes,
    contentLevel,
    setContentLevel,
    presentationSelectedColor,
    setPresentationSelectedColor,
    presentationCustomColor,
    setPresentationCustomColor,
    pptUrl,
    setPptUrl,
    openPptViewDialog,
    setOpenPptViewDialog,
    selectedSections,
    setSelectedSections,
    openSlideEditorView,
    setOpenSlideEditorView,
    generatedSlides,
    setGeneratedSlides,
    publicationTitle,
    setPublicationTitle,
    slideGenerationProgress,
    setSlideGenerationProgress,
    isGeneratingSlides,
    setIsGeneratingSlides,
    pptId,
    setPptId,

    // Poster states
    openPosterDialog,
    setOpenPosterDialog,
    posterSections,
    setPosterSections,
    posterLanguage,
    setPosterLanguage,
    addReferences,
    setAddReferences,
    posterContentLevel,
    setPosterContentLevel,
    posterSelectedColor,
    setPosterSelectedColor,
    posterCustomColor,
    setPosterCustomColor,
    posterUrl,
    setPosterUrl,
    openPosterViewDialog,
    setOpenPosterViewDialog,

    // Audio states
    openAudioSummaryDialog,
    setOpenAudioSummaryDialog,
    audioDuration,
    setAudioDuration,
    audioLanguage,
    setAudioLanguage,
    audioVoice,
    setAudioVoice,
    audioLevel,
    setAudioLevel,
    audioSummaryUrl,
    setAudioSummaryUrl,
    openAudioViewDialog,
    setOpenAudioViewDialog,

    // Images states
    allFigures,
    setAllFigures,
    openImagesDialog,
    setOpenImagesDialog,

    // Carousel states
    carouselUrl,
    setCarouselUrl,
    openCarouselViewDialog,
    setOpenCarouselViewDialog,

    // Instapost states
    instapostUrl,
    setInstapostUrl,
    openInstapostViewDialog,
    setOpenInstapostViewDialog,
    openInstapostDialog,
    setOpenInstapostDialog,
    postSection,
    setPostSection,
    postContentLevel,
    setPostContentLevel,
    postSelectedColor,
    setPostSelectedColor,
    postCustomColor,
    setPostCustomColor,

    // Dialog & navigation
    isGenerating,
    blocker,
    currentPublicationTaskStatus,
  };
};
